import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import {
  EXPERIMENT_VERSION_MAX_LENGTH,
  EXPERIMENT_VERSION_PATTERN,
} from '../experiments/version-pattern';
import type { WithdrawalEscalationMinRisk } from '../programs/schema';

// Wire schemas for the onboarding-sessions resource group (ADR-002 envelope).
// The status enum is the single source of truth for the allowed set (ADR-056
// Rule 8): the `onboarding_sessions_status_check` constraint mirrors these
// values, and every wire schema derives from this enum — never a bare string.
export const ONBOARDING_STATUS = ['in_progress', 'completed'] as const;

/**
 * Longest stored onboarding flow version. It is the experiment version bound,
 * so every version the server accepts also fits the column's CHECK and a valid
 * value can never fail the write at the database.
 */
export const ONBOARDING_FLOW_VERSION_MAX_LENGTH = EXPERIMENT_VERSION_MAX_LENGTH;
export const onboardingStatusSchema = z.enum(ONBOARDING_STATUS);
export type OnboardingStatus = z.infer<typeof onboardingStatusSchema>;

// How a session's `completed_at` was established. `observed` is the instant
// the live finalize wrote; `backfill_updated_at` is the best proxy for a
// session completed before the column existed; `migrated_unknown` is an
// imported completion whose real time the source system never recorded, so
// the timestamp stays null rather than carrying the import's own time.
export const ONBOARDING_COMPLETION_BASIS = [
  'observed',
  'backfill_updated_at',
  'migrated_unknown',
] as const;
export const onboardingCompletionBasisSchema = z.enum(ONBOARDING_COMPLETION_BASIS);
export type OnboardingCompletionBasis = z.infer<typeof onboardingCompletionBasisSchema>;

/**
 * The GenUI intake flow's `flow_id` — the flow every member completes before
 * reaching the home surface. Shared so the mobile flow definition and any
 * server-side consumer bind to one constant instead of coincidentally-matching
 * literals; a mobile contract
 * test asserts the flow definition uses this value.
 */
export const ONBOARDING_INTAKE_FLOW_ID = 'onboarding-flow';

/**
 * The CAB (Sona-handoff) onboarding flow's `flow_id`. Outside QA seed tooling,
 * a session row with this flow id is created only for demo-minted members
 * (the seeded-member mint), which is why the server treats its existence as
 * the discriminator for demo-scoped behaviour such as feedback suppression.
 * Shared so the seed preset, the suppression rule, and the mobile gate bind to
 * one constant instead of coincidentally-matching literals.
 */
export const CAB_ONBOARDING_FLOW_ID = 'cab-onboarding-flow';

/**
 * URL-safe slug for the `{flowId}` path segment (no slashes, spaces, or encoded
 * characters; non-empty; bounded length). Validated before any DB access so a
 * traversal/encoded value is rejected at the boundary. Tighten to the GenUI
 * flow-id registry's canonical form once that registry exists.
 */
export const flowIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/, 'flowId must be a URL-safe slug');

/** Member-supplied onboarding answers: an arbitrary JSON object, shallow-merged server-side. */
export const onboardingAnswersSchema = z.record(z.string(), z.unknown());
export type OnboardingAnswers = z.infer<typeof onboardingAnswersSchema>;

/** Shared write-boundary validation for every questionnaire completion transport. */
export const validatedOnboardingAnswersSchema = onboardingAnswersSchema.superRefine(
  (answers, ctx) => {
    // The one answer key with a closed vocabulary the server derives from: a
    // malformed selection is rejected at the boundary (400 through the existing
    // envelope) rather than stored and silently read as "no symptoms". The
    // refinement adds nothing to the generated OpenAPI document.
    const symptoms = answers[WITHDRAWAL_SYMPTOMS_KEY];
    if (symptoms === undefined) return;
    const parsed = z.array(withdrawalSymptomSchema).safeParse(symptoms);
    if (!parsed.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [WITHDRAWAL_SYMPTOMS_KEY],
        message: 'withdrawal_symptoms must be an array of known symptom values',
      });
      return;
    }
    // The screen is mandatory and its answer key is what marks it answered, so
    // an empty selection must never persist: it would derive `none`, skip the
    // screen for good, and record no decision. The explicit `none` option is
    // the only way to say "no symptoms".
    if (parsed.data.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [WITHDRAWAL_SYMPTOMS_KEY],
        message: 'withdrawal_symptoms must contain at least one selection',
      });
    }
  },
);

/**
 * `POST /api/v1/onboarding/sessions/{flowId}/answers` body. The flow comes from
 * the path; the member from the JWT — neither is accepted here. Unknown keys
 * (e.g. a stray `memberId`) are stripped, not honored (anti-IDOR, ADR-028/045).
 */
export const appendAnswersRequestSchema = z.object({
  answers: validatedOnboardingAnswersSchema,
  finalize: z.boolean().optional().default(false),
  // The release version of the composition writing these answers. Accepted
  // as any value on purpose: it is bookkeeping, so a malformed one must never
  // turn a member's answers write into a 400. The server classifies it and
  // drops anything that is not a valid experiment version.
  flowVersion: z.unknown().optional(),
});
export type AppendAnswersRequest = z.infer<typeof appendAnswersRequestSchema>;

/**
 * The answers body as the API document describes it. The runtime schema accepts
 * any `flowVersion` so a bad value cannot fail the write; the document states
 * what a client is meant to send. The server also drops a version that the
 * experiment version schema's PHI check refuses; that check has no JSON Schema
 * form, so the document is a little looser than real acceptance.
 */
export const appendAnswersRequestDocumentSchema = z.object({
  answers: onboardingAnswersSchema,
  finalize: z.boolean().optional(),
  flowVersion: z
    .string()
    .max(ONBOARDING_FLOW_VERSION_MAX_LENGTH)
    .regex(EXPERIMENT_VERSION_PATTERN)
    .optional(),
});

/** The session row as returned to clients (timestamps as ISO-8601 strings). */
export const onboardingSessionDataSchema = z.object({
  id: z.string(),
  flowId: z.string(),
  memberId: z.string(),
  status: onboardingStatusSchema,
  /** Only exact-source app-setup completion emits this: true for the atomic
   * completion winner, false for a committed replay. Absent on legacy routes. */
  firstCompletion: z.boolean().optional(),
  answers: onboardingAnswersSchema,
  /**
   * Whether the client may present the post-session feedback screen for this
   * member. `false` only for members holding a `cab-onboarding-flow` session
   * row (demo-minted members — the CAB row is the server-side discriminator);
   * `true` for everyone else. Clients treat an absent or unreadable value as
   * `true` (fail-open: real members never lose the feedback surface).
   */
  showFeedback: z.boolean(),
  /**
   * The member's main program, present on the response that finalizes the
   * session. A check-in cannot be written without a member program, so a client
   * that saves the intake's baseline days needs this id in hand; enrollment is
   * ensured in the finalize request precisely so it can be returned here.
   * Absent on non-finalizing responses, and null when no program could be
   * resolved (the member answered nothing enrollable, or enrollment is still
   * only reachable through the async consumer).
   */
  memberProgramId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OnboardingSessionData = z.infer<typeof onboardingSessionDataSchema>;

export const onboardingSessionResponseSchema = apiEnvelope(onboardingSessionDataSchema);
export type OnboardingSessionResponse = z.infer<typeof onboardingSessionResponseSchema>;

export const clearOnboardingSessionDataSchema = z.object({
  cleared: z.literal(true),
});
export type ClearOnboardingSessionData = z.infer<typeof clearOnboardingSessionDataSchema>;

export const clearOnboardingSessionResponseSchema = apiEnvelope(clearOnboardingSessionDataSchema);
export type ClearOnboardingSessionResponse = z.infer<typeof clearOnboardingSessionResponseSchema>;

/**
 * Reference type naming a launched composed practice — the one reference
 * vocabulary the server verifies ownership for before persisting (a
 * client-supplied practice id must belong to the submitting member).
 */
export const COMPOSED_PRACTICE_REFERENCE_TYPE = 'composed_practice';

export const feedbackReferenceSchema = z
  .object({
    type: z.string().min(1).max(64),
    id: z.string().min(1).max(128),
  })
  .strict();
export type FeedbackReference = z.infer<typeof feedbackReferenceSchema>;

export const submitFeedbackRequestSchema = z
  .object({
    submissionKey: z.string().min(1).max(128),
    flowVersion: z.string().min(1).max(64).optional(),
    sourceScreenId: z.string().min(1).max(128),
    sourceVersion: z.string().min(1).max(64).optional(),
    score: z.number().min(0).max(1).optional(),
    rating: z.number().int().min(1).max(5).optional(),
    notes: z.string().max(1000).optional(),
    responses: z.record(z.string(), z.unknown()).optional().default({}),
    conversationId: z.string().min(1).max(128).optional(),
    /** The minted voice session this feedback follows — joins to the transcript pipeline. */
    sessionId: z.string().uuid().optional(),
    /** PostHog session replay id — forwarded to the feedback event for the Slack notifier link. */
    posthogSessionId: z.string().max(256).optional(),
    reference: feedbackReferenceSchema.optional(),
    /** Experiment variant key of the member's most recently started running experiment; null when not enrolled. Max 64 chars — variant keys are short enumerated strings. */
    cohort: z.string().max(64).nullable().optional(),
  })
  .strict();
export type SubmitFeedbackRequest = z.infer<typeof submitFeedbackRequestSchema>;

export const feedbackSubmissionDataSchema = z.object({
  id: z.string(),
  flowId: z.string(),
  submissionKey: z.string(),
  sourceScreenId: z.string(),
  score: z.number().min(0).max(1).nullable(),
  rating: z.number().int().min(1).max(5).nullable(),
  createdAt: z.string(),
  replayed: z.boolean().default(false),
});
export type FeedbackSubmissionData = z.infer<typeof feedbackSubmissionDataSchema>;

export const feedbackSubmissionResponseSchema = apiEnvelope(feedbackSubmissionDataSchema);
export type FeedbackSubmissionResponse = z.infer<typeof feedbackSubmissionResponseSchema>;

// ─── answer-value code vocabularies ─────────────────────────────────────────
//
// Codes only. The goal and motivation ANSWERS are stored as machine codes
// (`cut_back`, `relationship`), and these enums are the declared vocabulary a
// reader resolves them through. The clinician-facing wording is NOT here: it
// lives with the surface that renders it (`apps/atlas/src/features/intake/lib/
// intake-labels.ts`), since ADR-017 covers this package and excludes that app.
//
// Stored `answers` is arbitrary jsonb, so a consumer may still meet a code from
// a newer flow version than these enums declare — it is the consumer's job to
// degrade that to a humanised form, never to a raw code, and never to a
// fabricated label.

/** Goal answer codes the intake flow writes (`buildGoalOptions`). */
export const ONBOARDING_GOAL = [
  'stop',
  'cut_back',
  'understand',
  'exploring',
  'sober',
  'unsure',
] as const;
export const onboardingGoalSchema = z.enum(ONBOARDING_GOAL);
export type OnboardingGoal = z.infer<typeof onboardingGoalSchema>;

/** Motivation answer codes; the flow collects these as a multi-select array. */
export const ONBOARDING_MOTIVATION = [
  'health',
  'relationship',
  'control',
  'provider',
  'curious',
] as const;
export const onboardingMotivationSchema = z.enum(ONBOARDING_MOTIVATION);
export type OnboardingMotivation = z.infer<typeof onboardingMotivationSchema>;

/**
 * Prior-change-attempt codes (`change_history`). Whether someone has tried before
 * is standard intake context for a first conversation, and the code alone
 * (`thought_about_it`) reads as nothing to a clinician.
 */
export const ONBOARDING_CHANGE_HISTORY = [
  'tried_before',
  'thought_about_it',
  'first_time',
] as const;
export const onboardingChangeHistorySchema = z.enum(ONBOARDING_CHANGE_HISTORY);
export type OnboardingChangeHistory = z.infer<typeof onboardingChangeHistorySchema>;

/**
 * Secondary-focus codes (`secondary_focus`) — a behaviour the member flagged
 * ALONGSIDE their main program, and never an enrollment.
 *
 * Two of these (`gambling`, `gaming`) happen to match `program_definitions` keys
 * and one (`eating`) does not, but that overlap is a coincidence of naming, not a
 * reference: the field is an ANSWER from a fixed option list, the same shape as
 * `goal` and `motivation`. Resolving it against the program catalog would read
 * meaning into the coincidence, and would leave the non-program option to fall
 * through as unrecognised — so it carries its own vocabulary here.
 *
 * The per-option cost that buys is bounded, not silent: the flow-side drift guard
 * compares this list to the flow's options in both directions, so a new option
 * cannot ship unlabelled. That is what separates this from a program-variant answer
 * like `tobacco_subtype`, which has no fixed list and is left to the generic
 * renderer precisely so a new program needs no client change.
 */
export const ONBOARDING_SECONDARY_FOCUS = ['gambling', 'gaming', 'eating'] as const;
export const onboardingSecondaryFocusSchema = z.enum(ONBOARDING_SECONDARY_FOCUS);
export type OnboardingSecondaryFocus = z.infer<typeof onboardingSecondaryFocusSchema>;

/**
 * The physical-dependence (withdrawal) check a manual-path substance member
 * answers instead of the Sona health-screening beat. The options are a fixed
 * list the member picks from, the same shape as `goal` and `motivation`, so
 * they carry their own vocabulary here; `severe` stands for seizures,
 * hallucinations or confusion (ever), and `none` is the explicit "none of
 * these" that clears the rest. Thresholds live server-side in
 * `deriveWithdrawalRisk`, never on a client (ADR-015).
 */
export const WITHDRAWAL_SYMPTOM = [
  'tremors',
  'sweating',
  'nausea',
  'anxiety',
  'insomnia',
  'racing_heart',
  'severe',
  'none',
] as const;
export const withdrawalSymptomSchema = z.enum(WITHDRAWAL_SYMPTOM);
export type WithdrawalSymptom = z.infer<typeof withdrawalSymptomSchema>;

/** The server's typed reading of a withdrawal answer set. */
export const WITHDRAWAL_RISK = ['none', 'positive', 'urgent'] as const;
export const withdrawalRiskSchema = z.enum(WITHDRAWAL_RISK);
export type WithdrawalRisk = z.infer<typeof withdrawalRiskSchema>;

/**
 * How the member chose to meet Sona at intake. `talk` and `text` open the
 * intake agent in that input mode; `none` records that the member declined the
 * Sona consent gate and finished onboarding without a conversation. Written by
 * the client into the finalized session's answers under `CONVERSATION_MODE_KEY`
 * and read by the API (the completion outcome's `conversation_mode` dimension)
 * and Atlas (the intake panel), so it is declared once here (ADR-066).
 */
export const CONVERSATION_MODE = ['talk', 'text', 'none'] as const;
export const conversationModeSchema = z.enum(CONVERSATION_MODE);
export type ConversationMode = z.infer<typeof conversationModeSchema>;
/** Client-written answer key carrying the mode. */
export const CONVERSATION_MODE_KEY = 'conversation_mode';
/**
 * The chartable reading of the stored mode: the vocabulary plus `unknown` for a
 * finalized session that carries no recognisable mode, so the forwarded
 * property is always present and bounded.
 */
export const conversationModeDimensionSchema = z.enum([...CONVERSATION_MODE, 'unknown']);
export type ConversationModeDimension = z.infer<typeof conversationModeDimensionSchema>;

/** Client-written: the selected symptoms. */
export const WITHDRAWAL_SYMPTOMS_KEY = 'withdrawal_symptoms';
/** Client-written: ISO instant of the answer. */
export const WITHDRAWAL_SCREENED_AT_KEY = 'withdrawal_screened_at';
/** Server-written only; an incoming value under this key is dropped before the merge. */
export const WITHDRAWAL_RISK_KEY = 'withdrawal_risk';

/**
 * Derive the risk from a symptom selection. `urgent` when the severe option is
 * present, `positive` when any other known symptom is, otherwise `none`.
 * Unknown strings are ignored rather than rejected: the wire schema already
 * validates the array, and a reader of stored answers must not fail on a value
 * a later vocabulary added.
 */
export function deriveWithdrawalRisk(symptoms: readonly string[]): WithdrawalRisk {
  const known = symptoms.filter(
    (value): value is WithdrawalSymptom => withdrawalSymptomSchema.safeParse(value).success,
  );
  if (known.includes('severe')) return 'urgent';
  if (known.some((value) => value !== 'none')) return 'positive';
  return 'none';
}

/**
 * Whether a derived withdrawal risk raises the intake safety escalation under a
 * program's threshold. `urgent` always raises, so no program setting can drop a
 * seizure, hallucination or confusion report; `none` never raises; `positive`
 * raises unless the program asks for `urgent` only. An absent threshold reads
 * as `positive`, so a missing or unreadable setting can only over-escalate.
 */
export function meetsWithdrawalEscalationThreshold(
  risk: WithdrawalRisk,
  minRisk: WithdrawalEscalationMinRisk | undefined,
): boolean {
  if (risk === 'none') return false;
  if (risk === 'urgent') return true;
  return minRisk !== 'urgent';
}

// ─── normalised answer contract ─────────────────────────────────────────────
//
// The typed read of `answers`: the four judgements a consumer would otherwise
// make itself, already made server-side. The stored record still ships verbatim
// beside this — a consumer that can interpret a shape the server could not must
// still be able to reach it.
//
// Structure only. No display text crosses this contract: what a stored code
// MEANS in words is presentation, and it stays with the surface that renders it.
// The server's job here is the part a consumer cannot safely re-derive — which
// of four persisted key shapes an answer is under, whether a flow tool or the
// member wrote it, whether it is an answer at all, and every scalar under it
// exactly once.

/**
 * Whose output a stored answer key holds.
 *
 * `derived` means a flow tool wrote it — a week-summary view-model, generated
 * summary prose, the anchor date stamping the day window — and the member was
 * never asked the question. Decided from the flow definition's own declared
 * tool write targets (`./derived-targets`), never guessed from the key name.
 */
export const ANSWER_ORIGIN = ['member', 'derived'] as const;
export const answerOriginSchema = z.enum(ANSWER_ORIGIN);
export type AnswerOrigin = z.infer<typeof answerOriginSchema>;

/** A primitive leaf of a stored answer value. `null` is absence, not a leaf. */
export const answerScalarSchema = z.union([z.string(), z.number(), z.boolean()]);
export type AnswerScalar = z.infer<typeof answerScalarSchema>;

/**
 * One scalar leaf of a stored value, with where inside that value it sits.
 *
 * Per SCALAR rather than per key, because that is the unit the coverage
 * guarantee is made in: a key can hold a list, and accounting for part of that
 * list while claiming the key is the mistake this shape makes unexpressible.
 *
 * Carries NO display text. Deciding that `cut_back` reads "Wants to cut back"
 * is presentation, and it stays with the surface that renders it: the wording
 * is static English for one internal staff tool, it does not vary by client,
 * environment or programme launch, and putting it here would drag every such
 * string through the localization catalog and into every shipped client bundle.
 * What the server owes a consumer is the STRUCTURE — which key this is, whose
 * output it is, whether it is an answer, and every scalar under it exactly once.
 *
 * Deliberately NOT `.strict()`, here or on the entry below.
 *
 * Both are children of a response Atlas parses, and a strict boundary nests: a
 * strict child rejects the whole response on an unknown key even when the
 * parent is lenient, so an installed bundle breaks on the next additive field
 * rather than ignoring it. Zod strips unknown keys by default, which is the
 * forward-compatible behaviour a response wants. Strictness belongs on request
 * schemas, where the server rejecting junk input is the point.
 *
 * What the wire actually carries is pinned where it is produced rather than
 * where it is parsed — see the emitted-key-set assertion in `normalise.test.ts`.
 */
export const normalisedAnswerScalarSchema = z.object({
  /** RFC 6901-style pointer INTO the stored value; `''` when the value is itself a scalar. */
  path: z.string(),
  value: answerScalarSchema,
});
export type NormalisedAnswerScalar = z.infer<typeof normalisedAnswerScalarSchema>;

/**
 * One answer, normalised.
 *
 * `value` is the stored value verbatim and is always present — it is what keeps
 * a value the server could not interpret reachable rather than silently
 * dropped. It is typed `unknown` because the column genuinely is: this contract
 * resolves who wrote the key and what its scalars are, not what shape a future
 * flow version stores.
 */
export const normalisedOnboardingAnswerSchema = z
  .object({
    /** The canonical flat key, with the four persisted spellings already collapsed. */
    key: z.string(),
    origin: answerOriginSchema,
    /** Whether the stored value is an answer at all — `0`/`false` yes, `''`/`[]`/`{}` no. */
    answered: z.boolean(),
    value: z.unknown(),
    /** Every scalar leaf of `value`, in stored order, one-to-one with what the record holds. */
    scalars: z.array(normalisedAnswerScalarSchema),
  })
  // `z.unknown()` infers its key as OPTIONAL, so without this the contract
  // would accept an entry carrying no `value` at all — precisely the shape the
  // "a value the consumer cannot interpret stays reachable" guarantee exists to
  // forbid. The field has to stay `unknown` (stored jsonb really is any JSON
  // value, `null` included), so presence is asserted rather than typed.
  .superRefine((entry, ctx) => {
    if (!('value' in entry)) {
      ctx.addIssue({
        code: 'custom',
        path: ['value'],
        message: 'value is required: an entry must carry its stored value verbatim',
      });
    }
  });
/**
 * The entry type, with `value` REQUIRED.
 *
 * `z.infer` alone would type it `value?: unknown`, because `z.unknown()` admits
 * `undefined` and Zod therefore infers an optional key. The `.superRefine`
 * above closes the runtime hole but cannot repair the inferred shape, so the
 * exported type would still tell a consumer the stored value might be absent —
 * and a consumer compiling missing-value handling is exactly how an
 * uninterpretable answer gets dropped instead of carried forward. The boundary
 * owns the uncertainty (ADR-117): `value` is `unknown` because the column
 * genuinely is, and it is always THERE.
 */
export type NormalisedOnboardingAnswer = Omit<
  z.infer<typeof normalisedOnboardingAnswerSchema>,
  'value'
> & { value: unknown };
